import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { currentIdToken } from '../auth/cognito';
import { useGameSocket } from '../ws/useGameSocket';
import { useGameStore } from '../store';
import { useCountdown } from '../hooks/useCountdown';
import { Action, shoutPoints, PublicQuestion, QueueEntry, ScoreEntry } from '../shared/protocol';
import { JoinQR } from '../components/JoinQR';
import { VotePie, VoteSummary, useVoteLabels } from '../components/VotePie';
import { QuestionMedia } from '../components/QuestionMedia';
import { SidePictures, SideAudioHost, sidesAreImages, sidesAreAudio } from '../components/SideMedia';
import { useT, TFunc } from '../i18n';

// Seconds relative to the tier reference (first correct buzz), e.g. "+1.2s".
const formatDelta = (ms: number) => `${ms > 0 ? '+' : ''}${(ms / 1000).toFixed(1)}s`;

export function AdminConsole() {
  const t = useT();
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) navigate('/admin', { replace: true });
      else setToken(t);
    });
  }, [navigate]);

  const socket = useGameSocket(token && gameId ? { role: 'admin', token, gameId } : null);
  const { connected, state, calledOn, lastResult, error } = useGameStore();
  const open = state?.questionState === 'open';
  const remaining = useCountdown(open ? state?.endsAt : undefined);
  const shout = state?.questionMode === 'shout';
  const vote = state?.kind === 'normcringe';
  const labels = useVoteLabels(state?.voteLabels);
  const scored = !!state?.scores?.some((sc) => sc.score > 0);
  const neutral = !!state?.voteLabels;
  const windowS = state?.windowSeconds ?? 30;
  // Spoiler guard for when the console is projected: hides the answer note and
  // the correct side until the round is closed. Remembered per browser.
  const [hideAnswers, setHideAnswers] = useState(() => {
    try { return localStorage.getItem('beernerd.hideAnswers') === '1'; } catch { return false; }
  });
  const toggleHide = () => {
    const next = !hideAnswers;
    setHideAnswers(next);
    try { localStorage.setItem('beernerd.hideAnswers', next ? '1' : '0'); } catch { /* ignore */ }
  };
  const revealed = state?.questionState === 'closed';

  const [showQR, setShowQR] = useState(true);
  const [copied, setCopied] = useState(false);
  // Shouting-match winner selection (host multi-selects from the roster).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelected = (playerId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(playerId) ? next.delete(playerId) : next.add(playerId);
      return next;
    });
  // Clear the selection whenever the question is re-armed (idle) or we move on.
  const questionKey = `${state?.question?.id ?? ''}:${state?.questionState ?? ''}`;
  useEffect(() => {
    if (state?.questionState === 'idle') setSelected(new Set());
  }, [questionKey, state?.questionState]);

  const send = (action: string, data?: unknown) => socket?.send(action, data);

  const joinLink = state ? `${window.location.origin}/play/${state.code}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is shown next to the button anyway */
    }
  };

  return (
    <div className={`stack ${neutral ? 'neutral' : ''}`}>
      <div className="rowbetween">
        <div className="row">
          <Link to="/admin/games" className="ghost">{t('console.backGames')}</Link>
          {state && <span className="pill"><strong>{state.code}</strong></span>}
        </div>
        <span className={connected ? 'status ok' : 'status bad'}>{connected ? t('console.live') : t('console.connecting')}</span>
      </div>

      {error && <div className="banner error">{t(error)}</div>}
      {!state && <div className="card centered"><p className="muted">{t('console.loading')}</p></div>}

      {state && state.status !== 'finished' && (
        <div className="card">
          <div className="sharehead">
            <a href={joinLink} className="joinlink" title={joinLink}>{joinLink.replace(/^https?:\/\//, '')}</a>
            <span className="sharebtns">
              <button className="ghost small nowrap" onClick={copyLink}>{copied ? t('console.copied') : t('console.copyLink')}</button>
              <button className="ghost small nowrap" onClick={() => setShowQR((v) => !v)}>{showQR ? t('console.hideQR') : t('console.showQR')}</button>
            </span>
          </div>
          {showQR && <JoinQR code={state.code} />}
        </div>
      )}

      {state && (
        <>
          {state.status === 'lobby' && (
            <div className="card centered">
              <h2>{t('console.waitingPlayers')}</h2>
              <p className="muted">{t('console.pressStart')}</p>
            </div>
          )}

          <div className="card">
            <div className="rowbetween">
              <div className="muted topic-title">{state.topicTitle ?? '—'}</div>
              <div className="muted small">{t('console.stateLabel', { status: t(`status.${state.status}`), qstate: t(`qstate.${state.questionState}`) })}</div>
            </div>

            {state.question ? (
              <>
                <h2 className="question">{state.question.text}</h2>
                <QuestionMedia q={state.question} host />
                {vote && sidesAreImages(state.question.sides) && (
                  <SidePictures sides={state.question.sides!} labels={labels} correct={state.questionState === 'closed' ? state.votes?.correct : undefined} />
                )}
                {vote && sidesAreAudio(state.question.sides) && (
                  <SideAudioHost sides={state.question.sides!} labels={labels} correct={state.questionState === 'closed' ? state.votes?.correct : undefined} />
                )}
                <div className="rowbetween">
                  {vote ? <span /> : <span className="points">{t('play.pts', { n: state.question.points })}</span>}
                  {open && <span className="timer">{remaining}s</span>}
                </div>
                {state.answerNote && !hideAnswers && <div className="answer">{t('console.answer', { answer: state.answerNote })}</div>}
                {vote && state.votes?.correct && (!hideAnswers || revealed) && (
                  <div className="answer">{t('play.correctAnswer', { answer: state.votes.correct === 'norm' ? labels.norm : labels.cringe })}</div>
                )}
              </>
            ) : (
              <p className="muted">{t('console.noQuestion')}</p>
            )}

            <div className="controls">
              {state.status === 'lobby' && (
                <button className="primary" onClick={() => send(Action.AdminStart)}>{t('console.startGame')}</button>
              )}
              {state.status === 'active' && (
                <>
                  {/* Sticky game-level mode switch (quiz only). Locked while a buzzer
                      is open so a round always resolves under one rule set. */}
                  {!vote && (
                  <span className="modetoggle" role="group" aria-label={t('console.mode')}>
                    <button
                      className={!shout ? 'toggle on' : 'toggle'}
                      disabled={open}
                      onClick={() => send(Action.AdminSetMode, { mode: 'classic' })}
                    >
                      {t('console.modeClassic')}
                    </button>
                    <button
                      className={shout ? 'toggle on' : 'toggle'}
                      disabled={open}
                      onClick={() => send(Action.AdminSetMode, { mode: 'shout' })}
                    >
                      {t('console.modeShout')}
                    </button>
                  </span>
                  )}
                  {/* Buzzer only opens for a fresh (idle) question. Once it has run
                      or been answered, it must be explicitly reset to run again. */}
                  {state.question && state.questionState === 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminOpenQ)}>
                      {vote ? t('console.openVote', { s: windowS }) : t('console.openBuzzer', { s: windowS })}
                    </button>
                  )}
                  {state.question && state.questionState !== 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminResetQ)}>
                      {vote ? t('console.resetVote') : t('console.resetBuzzer')}
                    </button>
                  )}
                  <span className="navgroup">
                    <button className="ghost" title={t('nav.prevTopic')} onClick={() => send(Action.AdminPrevTop)}>◀◀</button>
                    <button className="ghost" title={t('nav.prevQuestion')} onClick={() => send(Action.AdminPrevQ)}>◀</button>
                    <button className="ghost" title={t('nav.nextQuestion')} onClick={() => send(Action.AdminNextQ)}>▶</button>
                    <button className="ghost" title={t('nav.nextTopic')} onClick={() => send(Action.AdminNextTop)}>▶▶</button>
                  </span>
                  <button className="ghost" onClick={toggleHide} title={t('console.hideAnswersHelp')}>
                    {hideAnswers ? t('console.showAnswers') : t('console.hideAnswers')}
                  </button>
                  <button className="danger endbtn" onClick={() => { if (confirm(t('console.endGameConfirm'))) send(Action.AdminEndGame); }}>
                    {t('console.endGame')}
                  </button>
                </>
              )}
              {state.status === 'finished' && <p className="muted">{t('console.gameFinished')}</p>}
            </div>
          </div>

          {state.status === 'active' && vote && (
            <div className="card">
              <h3>{t('console.votes', { n: state.votes?.total ?? 0 })}</h3>
              {state.questionState === 'closed' && state.votes ? (
                <>
                  <VotePie tally={state.votes} labels={labels} />
                  {!!state.votes.awards?.length && (
                    <>
                      <h4 className="muted">{t('console.fastest', { max: windowS * 100 })}</h4>
                      <ol className="awards">
                        {state.votes.awards.map((a) => (
                          <li key={a.playerId}><span>{a.nickname}</span><span className="muted small">{(a.elapsedMs / 1000).toFixed(1)}s</span><span className="points">+{a.points}</span></li>
                        ))}
                      </ol>
                    </>
                  )}
                </>
              ) : state.questionState === 'open' ? (
                // Live count only — the split is revealed to the room when the
                // window closes, so keep the host's screen spoiler-free too.
                <p className="muted">{t('console.votesLive', { n: state.votes?.total ?? 0, total: state.scores?.length ?? 0 })}</p>
              ) : (
                <p className="muted">{t('console.votesIdle')}</p>
              )}
            </div>
          )}

          {state.status === 'finished' && vote && (
            <div className="card">
              <h3>{t('play.voteSummary')}</h3>
              <VoteSummary entries={state.voteSummary ?? []} labels={labels} />
            </div>
          )}

          {state.status === 'active' && shout && !vote && (
            <ShoutPanel
              question={state.question}
              queue={state.queue ?? []}
              roster={state.scores ?? []}
              selected={selected}
              onToggle={toggleSelected}
              onSelectShouted={(ids) => setSelected(new Set(ids))}
              onAward={(ids) => send(Action.AdminAward, { playerIds: ids })}
              t={t}
            />
          )}

          {state.status === 'active' && !shout && !vote && (
            <div className="card">
              <h3>{t('console.buzzedIn', { n: state.queue?.length ?? 0 })}</h3>
              {(!state.queue || state.queue.length === 0) ? (
                <p className="muted">{t('console.noBuzzes')}</p>
              ) : (
                <ol className="buzzqueue">
                  {state.queue.map((e) => (
                    <li key={e.playerId} className={`res-${e.result}`}>
                      <span className="qname">
                        {calledOn?.playerId === e.playerId && '📣 '}
                        {e.nickname}
                        {(e.deltaMs ?? 0) !== 0 && <span className="muted small"> {formatDelta(e.deltaMs)}</span>}
                        {e.result !== 'pending' && (
                          <em> — {t(`res.${e.result}`)}{e.result === 'correct' && (e.pointsAwarded ?? 0) > 0 ? ` (+${e.pointsAwarded})` : ''}</em>
                        )}
                      </span>
                      <span className="row">
                        {e.result === 'pending' ? (
                          <>
                            <button className="ghost" onClick={() => send(Action.AdminCallOn, { playerId: e.playerId })}>{t('console.callOn')}</button>
                            <button
                              className="ok"
                              disabled={(e.pointsOnCorrect ?? 0) === 0}
                              title={(e.pointsOnCorrect ?? 0) === 0 ? t('console.tooLate') : undefined}
                              onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: true })}
                            >
                              ✓{(e.pointsOnCorrect ?? 0) > 0 ? ` +${e.pointsOnCorrect}` : ''}
                            </button>
                            <button className="danger" onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: false })}>✗</button>
                          </>
                        ) : (
                          <button className="ghost" onClick={() => send(Action.AdminUnmark, { playerId: e.playerId })}>↩︎ {t('console.undo')}</button>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {lastResult && (
                <div className={`banner ${lastResult.correct ? 'success' : 'error'}`}>
                  {lastResult.nickname}: {lastResult.correct ? t('console.correct', { points: lastResult.points }) : t('console.wrong')}
                </div>
              )}
            </div>
          )}

          <div className="card">
            <h3>{vote && !scored ? t('console.players', { n: state.scores?.length ?? 0 }) : t('console.scores')}</h3>
            <ul className="scores">
              {(state.scores ?? []).map((s) => (
                <li key={s.playerId}><span>{s.nickname}</span>{(!vote || scored) && <span className="points">{s.score}</span>}</li>
              ))}
              {(state.scores ?? []).length === 0 && <li className="muted">{t('console.noPlayers')}</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

// ShoutPanel resolves a shouting-match question: the host multi-selects winners from
// the full roster (players who shouted are flagged 📣) and the question's points are
// split equally among them. Awarding is re-runnable — the backend refunds the prior
// award first, so the host can adjust the winners and award again.
function ShoutPanel({
  question,
  queue,
  roster,
  selected,
  onToggle,
  onSelectShouted,
  onAward,
  t,
}: {
  question?: PublicQuestion;
  queue: QueueEntry[];
  roster: ScoreEntry[];
  selected: Set<string>;
  onToggle: (playerId: string) => void;
  onSelectShouted: (playerIds: string[]) => void;
  onAward: (playerIds: string[]) => void;
  t: TFunc;
}) {
  const byPlayer = new Map(queue.map((e) => [e.playerId, e]));
  const shoutedIds = queue.filter((e) => e.result === 'pending').map((e) => e.playerId);
  const perWinner = shoutPoints(question?.points ?? 0, selected.size);

  return (
    <div className="card">
      <h3>{t('console.shoutTitle')}</h3>
      <p className="muted small">{t('console.shoutHint')}</p>
      {roster.length === 0 ? (
        <p className="muted">{t('console.noPlayers')}</p>
      ) : (
        <>
          {/* Controls sit above the roster so they stay reachable with many players. */}
          <div className="rowbetween shoutbar">
            <span>
              {shoutedIds.length > 0 && (
                <button className="ghost small" onClick={() => onSelectShouted(shoutedIds)}>
                  {t('console.selectShouted', { n: shoutedIds.length })}
                </button>
              )}
            </span>
            <button
              className="primary"
              disabled={selected.size === 0}
              onClick={() => onAward([...selected])}
            >
              {t('console.award', { n: selected.size, points: perWinner })}
            </button>
          </div>
          <div className="rosterlist">
            {roster.map((p) => {
              const q = byPlayer.get(p.playerId);
              const shouted = q?.result === 'pending';
              const awarded = q?.result === 'correct' ? q.pointsAwarded : 0;
              const isSel = selected.has(p.playerId);
              return (
                <button
                  key={p.playerId}
                  className={`rosteritem${isSel ? ' sel' : ''}`}
                  aria-pressed={isSel}
                  onClick={() => onToggle(p.playerId)}
                >
                  <span className="who">
                    <span className="check">{isSel ? '☑' : '☐'}</span>
                    <span>{p.nickname}</span>
                    {shouted && <span className="badge">📣</span>}
                  </span>
                  {awarded > 0 && <span className="points">+{awarded}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
